import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Link as LinkIcon,
  ShieldCheck,
  Smartphone,
  Users,
  CalendarSync,
  CheckCircle2,
  X,
  Star,
  ChevronDown,
  Menu,
  ArrowRight,
} from 'lucide-react';

/* ─────────────────────────── hooks ─────────────────────────── */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

/* ──────────── utilities ──────────── */

const formatCurrency = (n: number) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${n.toLocaleString('en-US')}`;
  }
};

function safeJsonLd(data: object): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

/* ──────────────────────── sub-components ───────────────────── */

function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 ${
        scrolled
          ? 'bg-[#FFFBF7]/90 backdrop-blur-md border-b border-[#EDE5DA]'
          : 'bg-transparent'
      }`}
    >
      <nav aria-label="Main navigation" className="max-w-6xl mx-auto flex items-center justify-between h-16 px-5">
        <Link to="/" className="font-display text-xl font-bold text-[#1B1612] tracking-tight">
          BookDay
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-[#3D3530] hover:text-[#1B1612] transition-colors">Features</a>
          <a href="#how-it-works" className="text-sm font-medium text-[#3D3530] hover:text-[#1B1612] transition-colors">How It Works</a>
          <a href="#pricing" className="text-sm font-medium text-[#3D3530] hover:text-[#1B1612] transition-colors">Pricing</a>
          <a href="#faq" className="text-sm font-medium text-[#3D3530] hover:text-[#1B1612] transition-colors">FAQ</a>
          <Link to="/expert/login" className="text-sm font-medium text-[#3D3530] hover:text-[#1B1612] transition-colors">Log in</Link>
          <Link
            to="/expert/signup"
            className="inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)' }}
          >
            Start Free
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 -mr-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div role="dialog" aria-modal="true" aria-label="Mobile navigation" className="md:hidden fixed inset-0 top-16 bg-[#FFFBF7] z-40 flex flex-col px-5 pt-8 pb-6 animate-fadeIn">
          <div className="flex flex-col gap-2">
            {[
              { href: '#features', label: 'Features' },
              { href: '#how-it-works', label: 'How It Works' },
              { href: '#pricing', label: 'Pricing' },
              { href: '#faq', label: 'FAQ' },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="text-lg font-medium text-[#1B1612] py-3 border-b border-[#EDE5DA]"
              >
                {item.label}
              </a>
            ))}
            <Link to="/expert/login" onClick={() => setMobileOpen(false)} className="text-lg font-medium text-[#1B1612] py-3 border-b border-[#EDE5DA]">
              Log in
            </Link>
          </div>
          <div className="mt-auto pt-6">
            <Link
              to="/expert/signup"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center rounded-xl py-4 text-base font-semibold text-white w-full"
              style={{ background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)' }}
            >
              Start Free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ──────────── Section 1: Hero ──────────── */

function HeroSection() {
  return (
    <section
      aria-labelledby="hero-heading"
      className="relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-24 px-5"
      style={{ background: 'linear-gradient(165deg, #FFFBF7 0%, #f3e8ff 35%, #e9d5ff 70%, #d8b4fe 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-8 items-center">
          {/* Copy */}
          <div className="max-w-xl">
            {/* Social proof pill */}
            <div className="hero-stagger-1 inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-[#EDE5DA] rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-[#3D3530]">Trusted by 2,000+ service professionals</span>
            </div>

            <h1
              id="hero-heading"
              className="hero-stagger-2 font-display text-[2.5rem] md:text-[3.5rem] lg:text-[4rem] font-bold text-[#1B1612] leading-[1.1] tracking-tight mb-6"
            >
              Get booked in{' '}
              <span className="text-purple-600">30 seconds</span>
            </h1>

            <p className="hero-stagger-3 text-lg md:text-xl text-[#3D3530] leading-relaxed mb-8 max-w-lg">
              The mobile-first booking platform designed for you. No commissions. No complicated setup. Your clients book without creating an account.
            </p>

            {/* CTAs */}
            <div className="hero-stagger-4 flex flex-col sm:flex-row gap-4">
              <Link
                to="/expert/signup"
                className="cta-pulse inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:scale-[1.02] active:translate-y-0 active:scale-100"
                style={{
                  background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)',
                  boxShadow: '0 8px 24px rgba(147, 51, 234, 0.35)',
                }}
              >
                Start Booking for Free
                <ArrowRight className="ml-2" size={18} />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-[#1B1612] border-2 border-[#1B1612] transition-all duration-200 hover:bg-[#1B1612] hover:text-[#FFFBF7]"
              >
                See How It Works
              </a>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="hero-stagger-5 flex justify-center md:justify-end">
            <div className="relative w-[280px] md:w-[320px]">
              {/* Phone frame */}
              <div className="bg-[#1B1612] rounded-[2.5rem] p-3 shadow-2xl">
                <div className="bg-white rounded-[2rem] overflow-hidden aspect-[9/19.5]">
                  {/* Simulated app UI */}
                  <div className="p-4 h-full flex flex-col">
                    {/* Status bar */}
                    <div className="flex justify-between items-center mb-4 text-xs text-[#6B5E54]">
                      <span>9:41</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-[#1B1612] rounded-sm" />
                        <div className="w-2 h-2 bg-[#1B1612] rounded-full" />
                      </div>
                    </div>
                    {/* App header */}
                    <div className="text-center mb-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 mx-auto mb-2" />
                      <p className="font-display text-sm font-bold text-[#1B1612]">Maria's Hair Studio</p>
                      <p className="text-xs text-[#6B5E54]">Hairdresser &middot; Brooklyn</p>
                    </div>
                    {/* Services */}
                    <div className="space-y-2 flex-1">
                      {[
                        { name: 'Haircut & Blowout', price: '$65', time: '45 min' },
                        { name: 'Coloring', price: '$120', time: '90 min' },
                        { name: 'Trim & Style', price: '$35', time: '30 min' },
                      ].map((s) => (
                        <div key={s.name} className="bg-purple-50 rounded-xl p-3 flex justify-between items-center">
                          <div>
                            <p className="text-xs font-semibold text-[#1B1612]">{s.name}</p>
                            <p className="text-[10px] text-[#6B5E54]">{s.time}</p>
                          </div>
                          <span className="text-xs font-bold text-pink-600">{s.price}</span>
                        </div>
                      ))}
                    </div>
                    {/* CTA button */}
                    <div
                      className="mt-3 rounded-xl py-2.5 text-center text-xs font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #9333ea, #db2777)' }}
                    >
                      Book Now
                    </div>
                  </div>
                </div>
              </div>
              {/* Glow */}
              <div className="absolute -inset-8 bg-purple-500/10 rounded-full blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────── Section 2: Social Proof Bar ──────────── */

function SocialProofBar() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section
      aria-label="Social proof statistics"
      className="bg-purple-50 border-y border-[#EDE5DA] py-6 px-5"
      ref={ref}
    >
      <div className={`max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-12 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={16} className="text-purple-600 fill-purple-500" />
            ))}
          </div>
          <span className="text-sm font-semibold text-[#1B1612]">4.8/5 rating</span>
        </div>
        <div className="hidden sm:block w-px h-6 bg-[#EDE5DA]" />
        <span className="text-sm font-medium text-[#3D3530]">
          Hairdressers, therapists, trainers & consultants save an average of <strong className="text-[#1B1612]">$8,400/year</strong>
        </span>
      </div>
    </section>
  );
}

/* ──────────── Section 3: Problem-Solution ──────────── */

function ProblemSolutionSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section aria-labelledby="problem-heading" className="bg-[#FFFBF7] py-16 md:py-24 px-5" ref={ref}>
      <div className={`max-w-6xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 id="problem-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-12 leading-tight max-w-3xl mx-auto">
          Tired of text message chaos, missed bookings, and{' '}
          <span className="text-purple-600">20% commissions?</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Problem */}
          <article aria-label="The old booking problem" className="bg-[#1B1612] rounded-2xl p-6 md:p-8 text-[#FFFBF7]">
            <h3 className="font-display text-lg font-bold mb-4 text-pink-400">The Old Way</h3>
            <ul className="space-y-3 text-sm leading-relaxed text-[#FFFBF7]/70">
              <li className="flex items-start gap-3">
                <X size={16} className="text-red-400 mt-0.5 shrink-0" />
                Clients text, call, and DM on three different platforms
              </li>
              <li className="flex items-start gap-3">
                <X size={16} className="text-red-400 mt-0.5 shrink-0" />
                Double bookings that make you look unprofessional
              </li>
              <li className="flex items-start gap-3">
                <X size={16} className="text-red-400 mt-0.5 shrink-0" />
                Fresha takes 20%. Booksy Boost takes 30%.
              </li>
              <li className="flex items-start gap-3">
                <X size={16} className="text-red-400 mt-0.5 shrink-0" />
                Clients abandon bookings because they don't want another account
              </li>
            </ul>
          </article>

          {/* Solution */}
          <article aria-label="The BookDay solution" className="bg-white rounded-2xl p-6 md:p-8 border border-[#EDE5DA]">
            <h3 className="font-display text-lg font-bold mb-4 text-purple-600">The BookDay Way</h3>
            <ul className="space-y-3 text-sm leading-relaxed text-[#3D3530]">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-purple-600 mt-0.5 shrink-0" />
                One app, one link. Clients book themselves in 30 seconds.
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-purple-600 mt-0.5 shrink-0" />
                No app download. No account. No friction.
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-purple-600 mt-0.5 shrink-0" />
                Zero commissions on every plan, forever.
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-purple-600 mt-0.5 shrink-0" />
                Manage your entire business from your phone.
              </li>
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}

/* ──────────── Section 4: Features ──────────── */

const FEATURES = [
  {
    icon: Zap,
    title: '30-Second Bookings',
    description: 'Your clients book in half a minute — no registration, no friction, just a couple taps on their phone.',
  },
  {
    icon: LinkIcon,
    title: 'No Account Needed',
    description: 'Share your unique link. Clients book instantly with just their name and phone — no passwords, no signup forms.',
  },
  {
    icon: ShieldCheck,
    title: 'Keep What You Earn',
    description: 'Zero commissions on bookings, ever. You keep 100% of your service fees. We make money from subscriptions, not your clients.',
  },
  {
    icon: Smartphone,
    title: 'Built for One Hand',
    description: 'Designed from the ground up for mobile. One-handed navigation, thumb-friendly buttons, crystal-clear interfaces.',
  },
  {
    icon: Users,
    title: 'Know Your Clients',
    description: 'See who\'s booking, add notes, track favorites, and build relationships that turn visitors into loyal regulars.',
  },
  {
    icon: CalendarSync,
    title: 'Calendar Sync',
    description: 'Sync with Google Calendar and never double-book again. Your schedule stays organized everywhere it matters.',
  },
];

function FeaturesSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section aria-labelledby="features-heading" id="features" className="bg-purple-50 py-16 md:py-24 px-5" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <h2 id="features-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-4 leading-tight">
          Everything you need, built for mobile
        </h2>
        <p className="text-center text-[#6B5E54] mb-12 max-w-xl mx-auto">
          Purpose-built for service professionals — not corporate meetings software.
        </p>

        <ul role="list" aria-label="BookDay features" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <li key={feature.title}>
                <article
                  className={`bg-white rounded-2xl p-6 md:p-8 h-full transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: isVisible ? `${i * 100}ms` : '0ms' }}
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-purple-600" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#1B1612] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#3D3530] leading-relaxed">{feature.description}</p>
                </article>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ──────────── Section 5: How It Works ──────────── */

const STEPS = [
  {
    num: '01',
    title: 'Set Your Schedule',
    description: 'Add your services, set your availability, and customize your booking page. Everything\'s optional — start simple.',
  },
  {
    num: '02',
    title: 'Share Your Link',
    description: 'Get a custom booking link like bookday.me/yourname. Drop it in your Instagram bio, text it to clients, print it on your card.',
  },
  {
    num: '03',
    title: 'Manage with One Tap',
    description: 'Bookings land in your calendar instantly. View client notes. Confirm or reschedule in seconds. Everything from your phone.',
  },
];

function HowItWorksSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section aria-labelledby="how-it-works-heading" id="how-it-works" className="bg-[#FFFBF7] py-16 md:py-24 px-5" ref={ref}>
      <div className="max-w-4xl mx-auto">
        <h2 id="how-it-works-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-4 leading-tight">
          Set up in under 5 minutes
        </h2>
        <p className="text-center text-[#6B5E54] mb-12 max-w-xl mx-auto">
          Three steps to take back control of your bookings.
        </p>

        <ol aria-label="Steps to get started with BookDay" className="space-y-8">
          {STEPS.map((step, i) => (
            <li
              key={step.num}
              className={`flex gap-6 items-start transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: isVisible ? `${i * 150}ms` : '0ms' }}
            >
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                <span className="font-mono text-lg font-bold text-purple-600">{step.num}</span>
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-[#1B1612] mb-1">{step.title}</h3>
                <p className="text-sm text-[#3D3530] leading-relaxed max-w-lg">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ──────────── Section 6: Testimonials ──────────── */

const TESTIMONIALS = [
  {
    quote: 'I was juggling Fresha and a Google Calendar and my phone was blowing up with texts. BookDay cut that down to one app. I\'m keeping the 20% I was losing to commissions — that\'s $12,000 a year back in my pocket.',
    name: 'Sarah Chen',
    role: 'Hair Stylist',
    location: 'Seattle, WA',
    rating: 5,
  },
  {
    quote: 'As a solopreneur, I need tools that work for me. BookDay is refreshingly simple. No marketplace pressure, no algorithm deciding who sees me. My clients book through my link, and it actually works on my phone.',
    name: 'Michael Rodriguez',
    role: 'Executive Coach',
    location: 'Austin, TX',
    rating: 5,
  },
  {
    quote: 'BookDay doesn\'t sell my client list to a marketplace. I control who I see, when I see them, and my clients aren\'t being upsold. The zero-commission model is the cherry on top.',
    name: 'Dr. Elena Vasquez',
    role: 'Licensed Massage Therapist',
    location: 'Miami, FL',
    rating: 5,
  },
  {
    quote: 'The no-login thing means my gym members just book on the spot — literally while they\'re in the gym. Conversions went up 40% because there\'s zero friction.',
    name: 'James Wilson',
    role: 'Personal Trainer',
    location: 'Denver, CO',
    rating: 4,
  },
];

function TestimonialsSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section
      aria-labelledby="testimonials-heading"
      className="py-16 md:py-24 px-5"
      style={{ background: 'linear-gradient(180deg, #1B1612 0%, #2A231B 100%)' }}
      ref={ref}
    >
      <div className="max-w-6xl mx-auto">
        <h2 id="testimonials-heading" className="font-display text-2xl md:text-4xl font-bold text-[#FFFBF7] text-center mb-12 leading-tight">
          What professionals are saying
        </h2>

        <ul
          role="list"
          aria-label="Customer testimonials"
          className={`grid sm:grid-cols-2 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {TESTIMONIALS.map((t) => (
            <li key={t.name}>
              <figure className="bg-[#FFFBF7]/[0.06] border border-[#FFFBF7]/10 rounded-2xl p-6 md:p-8 h-full flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < t.rating ? 'text-pink-400 fill-pink-400' : 'text-[#FFFBF7]/20'}
                    />
                  ))}
                </div>
                <blockquote className="flex-1">
                  <p className="font-display text-sm md:text-base italic text-[#FFFBF7] leading-relaxed">
                    "{t.quote}"
                  </p>
                </blockquote>
                <figcaption className="mt-6 pt-6 border-t border-[#FFFBF7]/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <strong className="text-sm font-semibold text-[#FFFBF7]">{t.name}</strong>
                    <p className="text-xs text-[#FFFBF7]/50">{t.role}, {t.location}</p>
                  </div>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ──────────── Section 7: Comparison Table ──────────── */

const COMPARISON_ROWS = [
  { feature: 'Monthly cost', bookday: 'Free – $29', fresha: '~$12', booksy: '$29.99', calendly: '$0–$16/user' },
  { feature: 'Commission', bookday: '0%', fresha: '20%', booksy: 'Up to 30%', calendly: 'N/A' },
  { feature: 'Mobile-first design', bookday: true, fresha: 'partial', booksy: 'partial', calendly: false },
  { feature: 'No client login', bookday: true, fresha: false, booksy: false, calendly: 'partial' },
  { feature: 'Service catalog', bookday: true, fresha: true, booksy: true, calendly: false },
  { feature: 'Client CRM', bookday: true, fresha: true, booksy: true, calendly: false },
  { feature: 'Cross-industry', bookday: true, fresha: false, booksy: false, calendly: true },
];

function renderCell(val: string | boolean) {
  if (val === true) return <CheckCircle2 size={18} className="text-purple-600 mx-auto" aria-label="Yes" />;
  if (val === false) return <X size={18} className="text-ink-light mx-auto" aria-label="No" />;
  if (val === 'partial') return <span className="text-xs text-ink-light">Partial</span>;
  return <span>{val}</span>;
}

function ComparisonSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section aria-labelledby="comparison-heading" className="bg-purple-50 py-16 md:py-24 px-5" ref={ref}>
      <div className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 id="comparison-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-4 leading-tight">
          How BookDay stacks up
        </h2>
        <p className="text-center text-[#6B5E54] mb-10 max-w-xl mx-auto">
          No hidden fees. No marketplace commissions. Just booking.
        </p>

        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full min-w-[600px] border-collapse">
            <caption className="sr-only">Feature comparison: BookDay versus major competitors</caption>
            <thead>
              <tr className="bg-[#1B1612] text-[#FFFBF7]">
                <th scope="col" className="text-left text-xs font-semibold uppercase tracking-wider px-4 py-3 rounded-tl-xl">Feature</th>
                <th scope="col" className="text-center text-xs font-semibold uppercase tracking-wider px-4 py-3 bg-purple-500/20">BookDay</th>
                <th scope="col" className="text-center text-xs font-semibold uppercase tracking-wider px-4 py-3">Fresha</th>
                <th scope="col" className="text-center text-xs font-semibold uppercase tracking-wider px-4 py-3">Booksy</th>
                <th scope="col" className="text-center text-xs font-semibold uppercase tracking-wider px-4 py-3 rounded-tr-xl">Calendly</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={row.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FFFBF7]'}>
                  <th scope="row" className="text-left text-sm font-medium text-[#1B1612] px-4 py-3">{row.feature}</th>
                  <td className="text-center text-sm font-semibold text-[#1B1612] px-4 py-3 bg-purple-50">{renderCell(row.bookday)}</td>
                  <td className="text-center text-sm text-[#3D3530] px-4 py-3">{renderCell(row.fresha)}</td>
                  <td className="text-center text-sm text-[#3D3530] px-4 py-3">{renderCell(row.booksy)}</td>
                  <td className="text-center text-sm text-[#3D3530] px-4 py-3">{renderCell(row.calendly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ──────────── Section 8: Savings Calculator ──────────── */

function SavingsCalculator() {
  const [revenue, setRevenue] = useState(40000);
  const { ref, isVisible } = useScrollReveal();

  const freshaLoss = Math.round(revenue * 0.2);
  const booksyLoss = Math.round(revenue * 0.3);

  return (
    <section aria-labelledby="calculator-heading" className="bg-[#FFFBF7] py-16 md:py-24 px-5" ref={ref}>
      <div className={`max-w-xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 id="calculator-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-4 leading-tight">
          How much could you keep?
        </h2>
        <p className="text-center text-[#6B5E54] mb-10">
          See how much you'd save by switching to BookDay.
        </p>

        <div className="bg-white rounded-3xl border-2 border-purple-200 shadow-xl p-8 md:p-12">
          <label className="block text-sm font-medium text-[#3D3530] mb-3">
            Your annual revenue from new clients
          </label>
          <input
            type="range"
            min={5000}
            max={200000}
            step={5000}
            value={revenue}
            onChange={(e) => setRevenue(Number(e.target.value))}
            aria-valuemin={5000}
            aria-valuemax={200000}
            aria-valuenow={revenue}
            aria-valuetext={`${formatCurrency(revenue)} per year`}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer mb-2"
            style={{
              background: `linear-gradient(to right, #9333ea ${((revenue - 5000) / 195000) * 100}%, #EDE5DA ${((revenue - 5000) / 195000) * 100}%)`,
            }}
          />
          <div className="text-right text-sm font-mono font-bold text-[#1B1612] mb-8">
            {formatCurrency(revenue)}/year
          </div>

          {/* Result */}
          <div className="text-center mb-8">
            <p className="text-sm text-[#6B5E54] mb-2">You'd save up to</p>
            <p className="font-mono text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent tracking-tight">
              {formatCurrency(booksyLoss)}
            </p>
            <p className="text-sm text-[#6B5E54] mt-2">per year with BookDay</p>
          </div>

          {/* Breakdown */}
          <div className="space-y-3 border-t border-[#EDE5DA] pt-6">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B5E54]">Fresha would charge (20%)</span>
              <span className="font-mono font-semibold text-red-500">{formatCurrency(freshaLoss)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B5E54]">Booksy Boost would charge (30%)</span>
              <span className="font-mono font-semibold text-red-500">{formatCurrency(booksyLoss)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B5E54]">BookDay commissions</span>
              <span className="font-mono font-bold text-emerald-600">$0</span>
            </div>
          </div>

          <Link
            to="/expert/signup"
            className="mt-8 w-full flex items-center justify-center rounded-xl py-4 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)' }}
          >
            Start Saving Today
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ──────────── Section 9: Pricing ──────────── */

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Start immediately, no credit card',
    features: ['Up to 3 services', 'Public booking link', 'Client management', 'Mobile app access'],
    cta: 'Start Free',
    popular: false,
  },
  {
    name: 'Pro',
    price: '$17',
    period: '/month',
    description: 'Everything you need to run your practice',
    features: ['Unlimited services', 'Google Calendar sync', 'Client notes & history', 'Custom booking page', 'Priority support'],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Business',
    price: '$29',
    period: '/month',
    description: 'For growing service professionals',
    features: ['Everything in Pro', 'Team members', 'Advanced analytics', 'Custom branding', 'API access'],
    cta: 'Start Free Trial',
    popular: false,
  },
];

function PricingSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section aria-labelledby="pricing-heading" id="pricing" className="bg-purple-50 py-16 md:py-24 px-5" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <h2 id="pricing-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-4 leading-tight">
          Simple, transparent pricing
        </h2>
        <p className="text-center text-[#6B5E54] mb-12">
          No hidden commissions. No surprise fees. Zero commission on every plan.
        </p>

        <ul
          role="list"
          aria-label="BookDay pricing plans"
          className={`grid md:grid-cols-3 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {PLANS.map((plan) => (
            <li key={plan.name}>
              <article
                aria-label={`${plan.name} plan`}
                className={`relative rounded-2xl p-6 md:p-8 h-full flex flex-col ${
                  plan.popular
                    ? 'bg-[#1B1612] text-[#FFFBF7] shadow-xl scale-[1.02]'
                    : 'bg-white border border-[#EDE5DA]'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="font-display text-xl font-bold mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-mono text-4xl font-bold">{plan.price}</span>
                  <span className={`text-sm ${plan.popular ? 'text-[#FFFBF7]/60' : 'text-[#6B5E54]'}`}>{plan.period}</span>
                </div>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-[#FFFBF7]/60' : 'text-[#6B5E54]'}`}>{plan.description}</p>
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${plan.popular ? 'text-pink-400' : 'text-purple-600'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/expert/signup"
                  className={`flex items-center justify-center rounded-xl py-3.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
                    plan.popular
                      ? 'text-white hover:shadow-lg'
                      : 'border-2 border-[#1B1612] text-[#1B1612] hover:bg-[#1B1612] hover:text-[#FFFBF7]'
                  }`}
                  style={plan.popular ? { background: 'linear-gradient(135deg, #9333ea, #db2777)' } : undefined}
                >
                  {plan.cta}
                </Link>
              </article>
            </li>
          ))}
        </ul>

        <p className="text-center text-xs text-[#9C8E82] mt-8">
          All plans include zero marketplace commissions. No credit card required to start.
        </p>
      </div>
    </section>
  );
}

/* ──────────── Section 10: FAQ ──────────── */

const FAQ_ITEMS = [
  {
    q: 'Do my clients need an account to book?',
    a: 'No. Your clients can book in seconds with just their name, email, and phone number. No password, no signup form, no app download. They click your link and book instantly.',
  },
  {
    q: 'What\'s the catch? How do you make money if there\'s no commission?',
    a: 'We charge a simple monthly fee for premium features, not a percentage of your earnings. That means we\'re aligned with your success — we want you to book more and earn more.',
  },
  {
    q: 'What if I\'m already using another platform?',
    a: 'Easy switch. Set up your services and availability in under 10 minutes. Share your new BookDay link with clients — you can transition gradually and shut down the old platform whenever you\'re ready.',
  },
  {
    q: 'Is my client data safe?',
    a: 'Your clients are yours. Full stop. We don\'t have a marketplace that sells your services, pushes clients to competitors, or monetizes your client list. Your data is encrypted and never shared.',
  },
  {
    q: 'How is this different from Calendly?',
    a: 'Calendly is built for scheduling corporate meetings. BookDay is purpose-built for service professionals with a service catalog, client CRM, mobile-first design, and zero commissions. Different tools for different work.',
  },
  {
    q: 'Does BookDay have a mobile app?',
    a: 'Yes. BookDay has a native iOS app and a mobile-optimized web platform. The entire product is designed for one-handed operation — unlike desktop-first tools like Calendly or Acuity.',
  },
];

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, isVisible } = useScrollReveal();

  return (
    <section aria-labelledby="faq-heading" id="faq" className="bg-purple-50 py-16 md:py-24 px-5" ref={ref}>
      <div className={`max-w-3xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 id="faq-heading" className="font-display text-2xl md:text-4xl font-bold text-[#1B1612] text-center mb-12 leading-tight">
          Questions? We've got answers.
        </h2>

        <dl>
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            const answerId = `faq-answer-${i}`;
            const questionId = `faq-question-${i}`;
            return (
              <div key={i} className="border-b border-[#EDE5DA]">
                <dt>
                  <h3>
                    <button
                      id={questionId}
                      className="w-full flex justify-between items-center py-5 md:py-6 text-left font-body text-base md:text-lg font-semibold text-ink pr-4"
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      aria-controls={answerId}
                    >
                      <span>{item.q}</span>
                      <ChevronDown
                        size={20}
                        className={`shrink-0 text-ink-light transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </h3>
                </dt>
                <dd
                  id={answerId}
                  role="region"
                  aria-labelledby={questionId}
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-[500px] opacity-100 pb-5 md:pb-6' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-sm md:text-base text-ink-body leading-relaxed">{item.a}</p>
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </section>
  );
}

/* ──────────── Section 11: Final CTA ──────────── */

function FinalCtaSection() {
  const { ref, isVisible } = useScrollReveal();
  return (
    <section
      aria-labelledby="final-cta-heading"
      className="relative overflow-hidden py-20 md:py-32 px-5"
      style={{ background: 'linear-gradient(180deg, #1B1612 0%, #2A231B 100%)' }}
      ref={ref}
    >
      <div className={`max-w-xl mx-auto text-center space-y-6 md:space-y-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 id="final-cta-heading" className="font-display text-[2rem] md:text-[3rem] font-bold text-[#FFFBF7] leading-[1.15]">
          Ready to take back your time?
        </h2>
        <p className="text-lg md:text-xl text-[#FFFBF7]/70 leading-relaxed">
          Join thousands of service professionals who stopped chasing bookings and started growing their business.
        </p>
        <div className="pt-2">
          <Link
            to="/expert/signup"
            className="inline-flex items-center justify-center rounded-xl px-10 py-5 text-lg font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:scale-[1.02] active:translate-y-0 active:scale-100"
            style={{
              background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)',
              boxShadow: '0 8px 32px rgba(147, 51, 234, 0.35)',
            }}
          >
            Start Free — No Credit Card Required
          </Link>
        </div>
        <p className="text-sm font-medium text-[#FFFBF7]/50">
          Cancel anytime. Zero commissions. Always.
        </p>
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
}

/* ──────────── Footer ──────────── */

function Footer() {
  return (
    <footer role="contentinfo" className="bg-[#1B1612] border-t border-[#FFFBF7]/10 py-8 px-5">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <span className="font-display text-sm font-bold text-[#FFFBF7]">BookDay</span>
          <nav aria-label="Footer navigation" className="flex gap-4">
            <Link to="/terms" className="text-xs text-[#FFFBF7]/50 hover:text-[#FFFBF7] transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="text-xs text-[#FFFBF7]/50 hover:text-[#FFFBF7] transition-colors">Privacy Policy</Link>
          </nav>
        </div>
        <p className="text-xs text-[#FFFBF7]/30">&copy; 2026 BookDay. All rights reserved.</p>
      </div>
    </footer>
  );
}

/* ──────────── Sticky Mobile CTA ──────────── */

function StickyMobileCta() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={`fixed bottom-0 inset-x-0 z-40 p-4 md:hidden backdrop-blur-md border-t transition-all duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      style={{ background: 'rgba(255, 251, 247, 0.92)', borderColor: '#EDE5DA' }}
    >
      <Link
        to="/expert/signup"
        className="w-full flex items-center justify-center rounded-xl py-4 text-base font-semibold text-white"
        style={{
          background: 'linear-gradient(135deg, #9333ea, #7e22ce, #db2777)',
          boxShadow: '0 4px 16px rgba(147, 51, 234, 0.35)',
        }}
      >
        Start Free
      </Link>
    </div>
  );
}

/* ──────────── JSON-LD Structured Data ──────────── */

function JsonLd() {
  const softwareApp = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BookDay',
    url: 'https://bookday.me',
    description: 'BookDay is a mobile-first appointment booking platform for independent service professionals. Manage services, availability, and client bookings with zero marketplace commissions.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: ['iOS', 'Web'],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier available. No marketplace commissions on any plan.',
    },
    featureList: [
      'Mobile-first appointment booking',
      'Zero marketplace commissions',
      'Public booking without client login',
      'Service catalog management',
      'Client CRM and history',
      'Google Calendar integration',
    ],
  };

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(softwareApp) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(faqPage) }} />
    </>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */

export default function LandingPage() {
  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-ink focus:font-semibold"
      >
        Skip to main content
      </a>
      <JsonLd />
      <NavBar />
      <main id="main-content">
        <HeroSection />
        <SocialProofBar />
        <ProblemSolutionSection />
        <FeaturesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <ComparisonSection />
        <SavingsCalculator />
        <PricingSection />
        <FaqSection />
        <FinalCtaSection />
      </main>
      <Footer />
      <StickyMobileCta />
      {/* Bottom spacer for sticky mobile CTA */}
      <div className="h-[76px] md:hidden" />
    </>
  );
}
